<script lang="ts">
  import '../app.css';
  import { page } from '$app/stores';
  import { authStore } from '$lib/stores/auth.store';
  import { sidebarOpen, sidebarCollapsed, toggleSidebar, toggleSidebarCollapsed } from '$lib/stores/ui.store';
  import { onMount } from 'svelte';
  import { afterNavigate } from '$app/navigation';
  import { goto } from '$app/navigation';
  import Toast from '$lib/components/ui/Toast.svelte';
  import SubscriptionBlocked from '$lib/components/ui/SubscriptionBlocked.svelte';
  import { subscriptionStore } from '$lib/stores/subscription.store';

  let { children } = $props();
  let currentPath = $derived($page.url.pathname);

  const publicRoutes = ['/login', '/'];

  let showAppShell = $derived(
    $authStore.isAuthenticated && !publicRoutes.includes(currentPath)
  );

  let subscriptionWarning = $derived.by(() => {
    const sub = $subscriptionStore;
    if (!sub) return null;
    if (sub.status === 'read_only') {
      return { tone: 'danger', text: 'Masa langganan berakhir. Aplikasi dalam mode baca saja — data tidak bisa ditambah atau diubah sampai langganan diperpanjang.' };
    }
    if (sub.status === 'active' && sub.daysLeft !== null && sub.daysLeft <= 7 && $authStore.user?.role === 'superadmin') {
      return { tone: 'warning', text: `Langganan ${sub.plan === 'trial' ? 'trial ' : ''}berakhir dalam ${sub.daysLeft} hari. Hubungi administrator untuk memperpanjang.` };
    }
    return null;
  });

  onMount(async () => {
    window.addEventListener('keydown', handleKeydown);

    await subscriptionStore.load();
    // While suspended every /api call is refused — don't treat that as a logged-out session.
    if ($subscriptionStore?.status === 'suspended') return;

    if (publicRoutes.includes(currentPath)) {
      return;
    }

    if (!$authStore.isAuthenticated) {
      goto('/login', { replaceState: true });
      return;
    }

    try {
      const result = await authStore.refreshUser();
      if (!result.success) {
        authStore.clear();
        goto('/login', { replaceState: true });
      }
    } catch (error) {
      authStore.clear();
      goto('/login', { replaceState: true });
    }

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });

  const menuItems = $derived([
    { path: '/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/employees', label: 'Karyawan', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    ...($authStore.user?.role === 'superadmin' ? [
      { path: '/branches', label: 'Cabang', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' }
    ] : []),
    { path: '/leaves', label: 'Cuti', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    ...($authStore.user?.role === 'superadmin' ? [
      { path: '/admins', label: 'Admin', icon: 'M12 4.354a4 4 0 110 7.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' }
    ] : []),
  ]);

  function isActive(path: string) {
    return currentPath.startsWith(path);
  }

  function handleLogout() {
    authStore.logout();
    goto('/login', { replaceState: true });
  }

  // Auto-close sidebar on mobile when navigating
  afterNavigate(() => {
    if (window.innerWidth < 1024) {
      sidebarOpen.set(false);
    }
  });

  // Close sidebar on Escape key (mobile)
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && $sidebarOpen) {
      sidebarOpen.set(false);
    }
  }
</script>

<Toast />

{#if $subscriptionStore?.status === 'suspended'}
  <SubscriptionBlocked info={$subscriptionStore} />
{:else if showAppShell}
  <div class="min-h-screen bg-gray-100">
    <!-- Backdrop for mobile sidebar -->
    {#if $sidebarOpen}
      <div
        class="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-200"
        role="button"
        tabindex="-1"
        onclick={() => sidebarOpen.set(false)}
        onkeydown={(e) => { if (e.key === 'Escape') sidebarOpen.set(false); }}
      ></div>
    {/if}

    <!-- Sidebar -->
    <aside
      class="fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transform transition-all duration-200 ease-in-out
      {$sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
      {$sidebarCollapsed ? 'w-16' : 'w-64'}"
    >
      <div class="flex flex-col h-full">
        <!-- Logo -->
        <div class="flex items-center justify-center h-16 border-b border-gray-200">
          {#if $sidebarCollapsed}
            <span class="text-lg font-bold text-primary-600">S</span>
          {:else}
            <h1 class="text-xl font-bold text-primary-600">SKAGE HRIS</h1>
          {/if}
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-2 py-6 space-y-1 overflow-y-auto">
          {#each menuItems as item}
            <a
              href={item.path}
              title={$sidebarCollapsed ? item.label : undefined}
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              {isActive(item.path)
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'}
              {$sidebarCollapsed ? 'justify-center' : ''}"
            >
              <svg class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={item.icon} />
              </svg>
              {#if !$sidebarCollapsed}
                {item.label}
              {/if}
            </a>
          {/each}
        </nav>

        <!-- User Info -->
        <div class="border-t border-gray-200 p-4">
          <a
            href="/profile"
            class="flex items-center gap-3 group cursor-pointer rounded-lg p-1 -m-1 hover:bg-gray-50 transition-colors"
            title={$sidebarCollapsed ? 'Profile' : undefined}
          >
            <div class="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span class="text-sm font-medium text-primary-700">
                {$authStore.user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            {#if !$sidebarCollapsed}
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate group-hover:text-primary-600">
                  {$authStore.user?.email || 'User'}
                </p>
                <p class="text-xs text-gray-500 capitalize">
                  {$authStore.user?.role || 'user'}
                </p>
              </div>
              <button
                type="button"
                class="text-gray-400 hover:text-gray-600"
                onclick={(e) => { e.preventDefault(); e.stopPropagation(); handleLogout(); }}
                title="Logout"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            {/if}
          </a>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="{$sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'} transition-all duration-200">
      <!-- Top Bar -->
      <header class="sticky top-0 z-40 bg-white border-b border-gray-200 h-16 flex items-center px-6">
        <button
          type="button"
          class="lg:hidden text-gray-500 hover:text-gray-700"
          onclick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <button
          type="button"
          class="hidden lg:flex text-gray-500 hover:text-gray-700"
          onclick={toggleSidebarCollapsed}
          title={$sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {#if $sidebarCollapsed}
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            {:else}
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            {/if}
          </svg>
        </button>
        <div class="flex-1"></div>
        <div class="flex items-center gap-3">
          <div class="text-sm text-gray-500 hidden sm:block">
            {new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <button
            type="button"
            class="lg:hidden text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onclick={handleLogout}
            title="Logout"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      {#if subscriptionWarning}
        <div class="px-6 py-2.5 text-sm {subscriptionWarning.tone === 'danger' ? 'bg-danger-50 text-danger-800 border-b border-danger-200' : 'bg-warning-50 text-warning-800 border-b border-warning-200'}">
          {subscriptionWarning.text}
        </div>
      {/if}

      <!-- Page Content -->
      <main class="p-6">
        {@render children?.()}
      </main>
    </div>
  </div>
{:else}
  {@render children?.()}
{/if}
